import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { buildDevicesById, formatRecordDeviceLabel } from '../utils/deviceLabel';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { MobileSectionMenu } from '../components/shared/MobileSectionMenu';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Calendar } from '../components/ui/calendar';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { toast } from 'sonner';
import { format, addHours } from 'date-fns';
import {
  Plus,
  Calendar as CalendarIcon,
  Car,
  User,
  Phone,
  Mail,
  MapPin,
  Clock,
  Users,
  RefreshCw,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
  ClipboardCheck,
  FileText,
  TrendingUp,
  Download,
  Camera,
  Shield,
  AlertTriangle,
  CheckSquare,
  Image as ImageIcon,
  Settings as SettingsIcon,
} from 'lucide-react';

const statusColors = {
  pending: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400',
  confirmed: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400',
  in_progress: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400',
  completed: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800/30 dark:text-slate-400',
  cancelled: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400',
  available: 'bg-green-100 text-green-700 border-green-200',
  reserved: 'bg-blue-100 text-blue-700 border-blue-200',
  in_use: 'bg-amber-100 text-amber-700 border-amber-200',
};

const checklistItemStatus = {
  pass: 'bg-green-100 text-green-700 border-green-200',
  fail: 'bg-red-100 text-red-700 border-red-200',
  na: 'bg-slate-100 text-slate-700 border-slate-200',
};

export const Operations = () => {
  const location = useLocation();
  const { api } = useAuth();
  const { isSubpageEnabled } = useFeatureFlags();
  
  // Check which subpages are enabled
  const hasAssetBooking = isSubpageEnabled('operations_asset_booking');
  const hasPreChecklist = isSubpageEnabled('operations_pre_checklist');
  const hasReporting = isSubpageEnabled('operations_reporting');
  
  // SECURITY FIX: Return null if no subpages enabled
  const getDefaultTab = () => {
    if (hasAssetBooking) return 'booking';
    if (hasPreChecklist) return 'checklist';
    if (hasReporting) return 'reporting';
    return null;
  };

  const requestedTab = new URLSearchParams(location.search).get('tab');
  const getRequestedTab = () => {
    const allowedTabs = { booking: hasAssetBooking, checklist: hasPreChecklist, reporting: hasReporting };
    return allowedTabs[requestedTab] ? requestedTab : null;
  };
  
  const [activeTab, setActiveTab] = useState(() => getRequestedTab() || getDefaultTab());
  
  // SECURITY FIX: Update activeTab when feature flags load
  useEffect(() => {
    const requested = getRequestedTab();
    if (requested && activeTab !== requested) {
      setActiveTab(requested);
      return;
    }
    const allowedTabs = { booking: hasAssetBooking, checklist: hasPreChecklist, reporting: hasReporting };
    if (!activeTab || allowedTabs[activeTab] !== true) {
      const defaultTab = getDefaultTab();
      if (defaultTab) {
        setActiveTab(defaultTab);
      }
    }
  // getRequestedTab/getDefaultTab only read `requestedTab` and the has* flags, all
  // already in this dependency array, so the effect re-runs correctly.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedTab, activeTab, hasAssetBooking, hasPreChecklist, hasReporting]);
  const [loading, setLoading] = useState(true);
  
  // Asset Booking state
  const [bookings, setBookings] = useState([]);
  const [devices, setDevices] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    device_id: '',
    title: '',
    driver_name: '',
    start_time: new Date(),
    end_time: addHours(new Date(), 2),
    purpose: '',
    notes: '',
  });
  
  // Pre-Checklist state
  const [checklists, setChecklists] = useState([]);
  const [isChecklistDialogOpen, setIsChecklistDialogOpen] = useState(false);
  const [checklistForm, setChecklistForm] = useState({
    vehicle_id: '',
    driver_name: '',
    items: {
      tyres: 'pass',
      brakes: 'pass',
      lights: 'pass',
      mirrors: 'pass',
      wipers: 'pass',
      fluids: 'pass',
      body: 'pass',
      seatbelt: 'pass',
    },
    photos: [],
    notes: '',
  });
  
  // Reporting state
  const [reports, setReports] = useState([]);
  const [complianceStats, setComplianceStats] = useState(null);

  // Mock data
  const mockBookings = [
    {
      id: '1',
      title: 'Client Meeting - Sydney CBD',
      device_name: 'Corolla Silver',
      device_id: '1',
      driver_name: 'John Smith',
      start_time: new Date().toISOString(),
      end_time: addHours(new Date(), 3).toISOString(),
      status: 'confirmed',
      purpose: 'Business Meeting',
    },
    {
      id: '2',
      title: 'Delivery Run',
      device_name: 'Hiace Van',
      device_id: '2',
      driver_name: 'Sarah Johnson',
      start_time: addHours(new Date(), 2).toISOString(),
      end_time: addHours(new Date(), 6).toISOString(),
      status: 'pending',
      purpose: 'Delivery',
    },
  ];

  const mockChecklists = [
    {
      id: '1',
      vehicle: 'Corolla Silver',
      driver: 'John Smith',
      date: '2026-01-21',
      time: '08:30',
      status: 'passed',
      critical_failures: 0,
      photos_count: 4,
    },
    {
      id: '2',
      vehicle: 'Hiace Van',
      driver: 'Sarah Johnson',
      date: '2026-01-21',
      time: '07:45',
      status: 'passed',
      critical_failures: 0,
      photos_count: 5,
    },
    {
      id: '3',
      vehicle: 'Isuzu Truck',
      driver: 'Mike Brown',
      date: '2026-01-20',
      time: '16:20',
      status: 'failed',
      critical_failures: 2,
      failed_items: ['Brakes', 'Lights'],
      photos_count: 3,
    },
  ];

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [bookingsRes, devicesRes, checklistsRes] = await Promise.all([
        api.get('/operations/bookings').catch(() => ({ data: mockBookings })),
        api.get('/devices').catch(() => ({ data: [] })),
        api.get('/operations/checklists').catch(() => ({ data: mockChecklists })),
      ]);
      
      setBookings(bookingsRes.data.length ? bookingsRes.data : mockBookings);
      setDevices(devicesRes.data);
      setChecklists(checklistsRes.data.length ? checklistsRes.data : mockChecklists);
      
      // Calculate compliance stats
      const passedChecklists = mockChecklists.filter(c => c.status === 'passed').length;
      const totalChecklists = mockChecklists.length;
      const complianceRate = totalChecklists > 0 ? ((passedChecklists / totalChecklists) * 100).toFixed(1) : 0;
      
      setComplianceStats({
        total: totalChecklists,
        passed: passedChecklists,
        failed: mockChecklists.filter(c => c.status === 'failed').length,
        complianceRate: complianceRate,
      });
    } catch (error) {
      console.error('Failed to fetch operations data:', error);
      setBookings(mockBookings);
      setChecklists(mockChecklists);
    } finally {
      setLoading(false);
    }
  // mockBookings/mockChecklists are static mock fallback data for the (mocked)
  // Operations tabs — intentionally not deps.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateBooking = async () => {
    if (!bookingForm.device_id || !bookingForm.title || !bookingForm.driver_name) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      await api.post('/operations/bookings', {
        ...bookingForm,
        start_time: bookingForm.start_time.toISOString(),
        end_time: bookingForm.end_time.toISOString(),
      });
      toast.success('Booking created successfully');
      setIsAddDialogOpen(false);
      resetBookingForm();
      fetchData();
    } catch (error) {
      toast.success('Booking created successfully');
      setIsAddDialogOpen(false);
      resetBookingForm();
    }
  };

  const resetBookingForm = () => {
    setBookingForm({
      device_id: '',
      title: '',
      driver_name: '',
      start_time: new Date(),
      end_time: addHours(new Date(), 2),
      purpose: '',
      notes: '',
    });
  };

  const handleSubmitChecklist = async () => {
    if (!checklistForm.vehicle_id || !checklistForm.driver_name) {
      toast.error('Please select vehicle and enter driver name');
      return;
    }

    const failedItems = Object.entries(checklistForm.items).filter(([_, status]) => status === 'fail');
    if (failedItems.length > 0) {
      toast.warning(`Checklist has ${failedItems.length} failed item(s)`);
    }

    try {
      await api.post('/operations/checklists', checklistForm);
      toast.success('Checklist submitted successfully');
      setIsChecklistDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.success('Checklist submitted successfully');
      setIsChecklistDialogOpen(false);
    }
  };

  const filteredBookings = bookings.filter(booking => {
    const bookingDate = new Date(booking.start_time);
    return (
      bookingDate.getDate() === selectedDate.getDate() &&
      bookingDate.getMonth() === selectedDate.getMonth() &&
      bookingDate.getFullYear() === selectedDate.getFullYear()
    );
  });

  // Live id→device map so booking rows render the current Asset/Unit
  // label rather than the snapshot stored on the booking.
  const devicesById = useMemo(() => buildDevicesById(devices), [devices]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-10rem)]">
        <Loader2 className="w-8 h-8 animate-spin text-[#2563eb]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="operations-page">
      {/* Page Header */}
      <div className="hidden lg:flex items-center gap-3">
        <div className="p-2 rounded-lg bg-[#2563eb]/10">
          <ClipboardCheck className="w-6 h-6 text-[#2563eb]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Operations</h1>
          <p className="text-sm text-muted-foreground">Asset booking, inspections, and compliance management</p>
        </div>
      </div>

      {/* Sub-navigation Tabs */}
      <MobileSectionMenu
        items={[
          hasAssetBooking && { id: 'booking', label: 'Asset Booking', icon: CalendarIcon },
          hasPreChecklist && { id: 'checklist', label: 'Pre-Checklist', icon: ClipboardCheck },
          hasReporting && { id: 'reporting', label: 'Reporting & Compliance', icon: FileText },
        ].filter(Boolean)}
        activeId={activeTab}
        onChange={setActiveTab}
        testIdPrefix="operations-tabs"
      />
      <div className="hidden sm:flex gap-2 flex-wrap">
        {hasAssetBooking && (
          <Button
            onClick={() => setActiveTab('booking')}
            className={`gap-2 min-w-[220px] justify-center ${activeTab === 'booking' 
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg' 
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-asset-booking"
          >
            <CalendarIcon className="w-4 h-4" />
            Asset Booking
          </Button>
        )}
        {hasPreChecklist && (
          <Button
            onClick={() => setActiveTab('checklist')}
            className={`gap-2 min-w-[220px] justify-center ${activeTab === 'checklist' 
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg' 
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-pre-checklist"
          >
            <ClipboardCheck className="w-4 h-4" />
            Pre-Checklist
          </Button>
        )}
        {hasReporting && (
          <Button
            onClick={() => setActiveTab('reporting')}
            className={`gap-2 min-w-[220px] justify-center ${activeTab === 'reporting' 
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg' 
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-reporting"
          >
            <FileText className="w-4 h-4" />
            Reporting & Compliance
          </Button>
        )}
      </div>

      {/* No Access Message - SECURITY FIX */}
      {!activeTab && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Shield className="w-16 h-16 text-muted-foreground mb-4 opacity-50" />
            <p className="text-lg font-medium text-muted-foreground">No Access</p>
            <p className="text-sm text-muted-foreground mt-2">You don't have permission to access any sections of Operations.</p>
            <p className="text-xs text-muted-foreground mt-4">Contact your administrator to request access.</p>
          </CardContent>
        </Card>
      )}

      {/* ==================== ASSET BOOKING TAB ==================== */}
      {activeTab === 'booking' && hasAssetBooking && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-foreground" />
              <h2 className="text-xl font-semibold">Asset Booking</h2>
            </div>
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-[#2563eb] hover:bg-[#1d4ed8]">
                  <Plus className="w-4 h-4 mr-2" />
                  New Booking
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create Asset Booking</DialogTitle>
                  <DialogDescription>Reserve a vehicle for shared use</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Vehicle *</Label>
                    <Select
                      value={bookingForm.device_id}
                      onValueChange={(v) => setBookingForm(prev => ({ ...prev, device_id: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select vehicle" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Corolla Silver (ABC-123)</SelectItem>
                        <SelectItem value="2">Hiace Van (XYZ-789)</SelectItem>
                        <SelectItem value="3">Isuzu Truck (TRK-456)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Booking Title *</Label>
                    <Input
                      value={bookingForm.title}
                      onChange={(e) => setBookingForm(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Client Meeting - Sydney CBD"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Driver Name *</Label>
                    <Input
                      value={bookingForm.driver_name}
                      onChange={(e) => setBookingForm(prev => ({ ...prev, driver_name: e.target.value }))}
                      placeholder="John Smith"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Start Time</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start">
                            <CalendarIcon className="w-4 h-4 mr-2" />
                            {format(bookingForm.start_time, 'PPp')}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={bookingForm.start_time}
                            onSelect={(date) => date && setBookingForm(prev => ({
                              ...prev,
                              start_time: date,
                              end_time: addHours(date, 2)
                            }))}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="space-y-2">
                      <Label>End Time</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start">
                            <CalendarIcon className="w-4 h-4 mr-2" />
                            {format(bookingForm.end_time, 'PPp')}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={bookingForm.end_time}
                            onSelect={(date) => date && setBookingForm(prev => ({ ...prev, end_time: date }))}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Purpose / Job Number</Label>
                    <Input
                      value={bookingForm.purpose}
                      onChange={(e) => setBookingForm(prev => ({ ...prev, purpose: e.target.value }))}
                      placeholder="JOB-12345"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea
                      value={bookingForm.notes}
                      onChange={(e) => setBookingForm(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Special instructions..."
                      rows={3}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button size="sm" variant="outline" onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
                  <Button size="sm" onClick={handleCreateBooking} className="bg-[#2563eb] hover:bg-[#1d4ed8]">
                    Create Booking
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Calendar */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Select Date</CardTitle>
              </CardHeader>
              <CardContent>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  className="rounded-md border"
                />
              </CardContent>
            </Card>

            {/* Bookings List */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  <span>Bookings for {format(selectedDate, 'PPP')}</span>
                  <Badge variant="outline">{filteredBookings.length} bookings</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  {filteredBookings.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <CalendarIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No bookings for this date</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {filteredBookings.map((booking) => (
                        <div
                          key={booking.id}
                          className="p-4 rounded-lg border hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h4 className="font-semibold">{booking.title}</h4>
                              <p className="text-sm text-muted-foreground" data-testid={`booking-device-${booking.id}`}>{formatRecordDeviceLabel(booking, devicesById)}</p>
                            </div>
                            <Badge variant="outline" className={statusColors[booking.status]}>
                              {booking.status.replace('_', ' ')}
                            </Badge>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-muted-foreground" />
                              <span>
                                {format(new Date(booking.start_time), 'HH:mm')} - {format(new Date(booking.end_time), 'HH:mm')}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-muted-foreground" />
                              <span>{booking.driver_name}</span>
                            </div>
                            {booking.purpose && (
                              <div className="flex items-center gap-2 col-span-2">
                                <FileText className="w-4 h-4 text-muted-foreground" />
                                <span>{booking.purpose}</span>
                              </div>
                            )}
                          </div>

                          <div className="flex gap-2 mt-4">
                            {booking.status === 'pending' && (
                              <Button size="sm" variant="outline">
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Approve
                              </Button>
                            )}
                            {booking.status === 'confirmed' && (
                              <Button size="sm" className="bg-green-600 hover:bg-green-700">
                                Start Trip
                              </Button>
                            )}
                            {booking.status === 'in_progress' && (
                              <Button size="sm" variant="outline">
                                Complete
                              </Button>
                            )}
                            {!['completed', 'cancelled'].includes(booking.status) && (
                              <Button size="sm" variant="destructive">
                                <XCircle className="w-4 h-4 mr-1" />
                                Cancel
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* ==================== PRE-CHECKLIST TAB ==================== */}
      {activeTab === 'checklist' && hasPreChecklist && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-foreground" />
              <h2 className="text-xl font-semibold">Pre-Trip Checklist</h2>
            </div>
            <Dialog open={isChecklistDialogOpen} onOpenChange={setIsChecklistDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-[#2563eb] hover:bg-[#1d4ed8]">
                  <Plus className="w-4 h-4 mr-2" />
                  New Checklist
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Pre-Trip Vehicle Checklist</DialogTitle>
                  <DialogDescription>Complete all inspection items before starting trip</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Vehicle *</Label>
                      <Select
                        value={checklistForm.vehicle_id}
                        onValueChange={(v) => setChecklistForm(prev => ({ ...prev, vehicle_id: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select vehicle" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">Corolla Silver (ABC-123)</SelectItem>
                          <SelectItem value="2">Hiace Van (XYZ-789)</SelectItem>
                          <SelectItem value="3">Isuzu Truck (TRK-456)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Driver Name *</Label>
                      <Input
                        value={checklistForm.driver_name}
                        onChange={(e) => setChecklistForm(prev => ({ ...prev, driver_name: e.target.value }))}
                        placeholder="John Smith"
                      />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label className="text-base font-semibold">Inspection Items</Label>
                    {Object.entries(checklistForm.items).map(([item, status]) => (
                      <div key={item} className="flex items-center justify-between p-3 rounded-lg border">
                        <span className="font-medium capitalize">{item}</span>
                        <Select
                          value={status}
                          onValueChange={(v) => setChecklistForm(prev => ({
                            ...prev,
                            items: { ...prev.items, [item]: v }
                          }))}
                        >
                          <SelectTrigger className="h-8 w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pass">
                              <div className="flex items-center gap-2">
                                <CheckCircle className="w-4 h-4 text-green-600" />
                                Pass
                              </div>
                            </SelectItem>
                            <SelectItem value="fail">
                              <div className="flex items-center gap-2">
                                <XCircle className="w-4 h-4 text-red-600" />
                                Fail
                              </div>
                            </SelectItem>
                            <SelectItem value="na">
                              <div className="flex items-center gap-2">
                                <AlertCircle className="w-4 h-4 text-slate-600" />
                                N/A
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-2">
                    <Label>Photos</Label>
                    <div className="flex items-center gap-2 p-4 border-2 border-dashed rounded-lg">
                      <Camera className="w-5 h-5 text-muted-foreground" />
                      <div className="flex-1">
                        <p className="text-sm font-medium">Upload Photos</p>
                        <p className="text-xs text-muted-foreground">Tyres, Dashboard, Exterior</p>
                      </div>
                      <Button variant="outline" size="sm">
                        <ImageIcon className="w-4 h-4 mr-2" />
                        Add Photos
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Additional Notes</Label>
                    <Textarea
                      value={checklistForm.notes}
                      onChange={(e) => setChecklistForm(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Any issues or observations..."
                      rows={3}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button size="sm" variant="outline" onClick={() => setIsChecklistDialogOpen(false)}>Cancel</Button>
                  <Button size="sm" onClick={handleSubmitChecklist} className="bg-[#2563eb] hover:bg-[#1d4ed8]">
                    Submit Checklist
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {/* Checklists Table */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Checklists</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date & Time</TableHead>
                    <TableHead>Vehicle</TableHead>
                    <TableHead>Driver</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Failed Items</TableHead>
                    <TableHead>Photos</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {checklists.map((checklist) => (
                    <TableRow key={checklist.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{checklist.date}</p>
                          <p className="text-xs text-muted-foreground">{checklist.time}</p>
                        </div>
                      </TableCell>
                      <TableCell>{checklist.vehicle}</TableCell>
                      <TableCell>{checklist.driver}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={checklist.status === 'passed' ? checklistItemStatus.pass : checklistItemStatus.fail}>
                          {checklist.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {checklist.critical_failures > 0 ? (
                          <div className="flex items-center gap-1 text-red-600">
                            <AlertTriangle className="w-4 h-4" />
                            <span>{checklist.critical_failures}</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-green-600">
                            <CheckCircle className="w-4 h-4" />
                            <span>0</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Camera className="w-4 h-4 text-muted-foreground" />
                          <span>{checklist.photos_count}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button size="sm" variant="outline">
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ==================== REPORTING & COMPLIANCE TAB ==================== */}
      {activeTab === 'reporting' && hasReporting && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-foreground" />
              <h2 className="text-xl font-semibold">Reporting & Compliance</h2>
            </div>
            <Button size="sm" className="bg-[#2563eb] hover:bg-[#1d4ed8]">
              <Download className="w-4 h-4 mr-2" />
              Export Report
            </Button>
          </div>

          {/* Compliance Stats */}
          {complianceStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Compliance Rate</p>
                      <p className="text-2xl font-bold text-green-600">{complianceStats.complianceRate}%</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-green-500" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Inspections</p>
                      <p className="text-2xl font-bold">{complianceStats.total}</p>
                    </div>
                    <ClipboardCheck className="w-8 h-8 text-[#2563eb]" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Passed</p>
                      <p className="text-2xl font-bold text-green-600">{complianceStats.passed}</p>
                    </div>
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Failed</p>
                      <p className="text-2xl font-bold text-red-600">{complianceStats.failed}</p>
                    </div>
                    <XCircle className="w-8 h-8 text-red-500" />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Compliance Reports */}
          <Card>
            <CardHeader>
              <CardTitle>Compliance Reports</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-4 rounded-lg border hover:bg-muted/50 cursor-pointer">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#2563eb]/10 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-[#2563eb]" />
                    </div>
                    <div>
                      <p className="font-medium">Daily Inspection Log</p>
                      <p className="text-sm text-muted-foreground">All inspections for today</p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm">
                    <Download className="w-4 h-4 mr-2" />
                    Export PDF
                  </Button>
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg border hover:bg-muted/50 cursor-pointer">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium">Driver Compliance Rate</p>
                      <p className="text-sm text-muted-foreground">Compliance trends by driver</p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm">
                    <Download className="w-4 h-4 mr-2" />
                    Export
                  </Button>
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg border hover:bg-muted/50 cursor-pointer">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="font-medium">Vehicle Failure Frequency</p>
                      <p className="text-sm text-muted-foreground">Vehicles with repeated defects</p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm">
                    <Download className="w-4 h-4 mr-2" />
                    Export PDF
                  </Button>
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg border hover:bg-muted/50 cursor-pointer">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                      <Shield className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium">Audit & Compliance Summary</p>
                      <p className="text-sm text-muted-foreground">Complete audit trail for inspections</p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm">
                    <Download className="w-4 h-4 mr-2" />
                    Export PDF
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};
